import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from 'react-native';
import { COLORS, FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';

import { tokens } from '@design';
// ============================================================
// Types
// ============================================================

export interface DropdownOption {
  value: string | number;
  label: string;
}

export interface SelectDropdownProps {
  options: DropdownOption[];
  value: string | number | null | undefined;
  onChange: (value: string | number) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  searchable?: boolean;
  maxHeight?: number;
  zIndex?: number;
}

// ============================================================
// Native Select Component - No z-index issues!
// ============================================================

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  required = false,
  disabled = false,
  error,
}) => {
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);

  // Use provided placeholder or default to translated value
  const displayPlaceholder = placeholder ?? t('common.select');

  // Theme colors
  const themeColors = {
    background: colors.inputBackground,
    backgroundDisabled: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    border: colors.border,
    textPrimary: colors.textPrimary,
    textPlaceholder: colors.textMuted,
    labelText: colors.textPrimary,
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    // Convert back to number if the original option value was a number
    const option = options.find(opt => String(opt.value) === newValue);
    if (option) {
      onChange(option.value);
    } else {
      onChange(newValue);
    }
  };

  return (
    <View style={styles.container}>
      {/* Label */}
      {label && (
        <View style={styles.labelRow}>
          {required && <Text style={styles.requiredStar}>*</Text>}
          <Text style={[styles.label, { color: themeColors.labelText }]}>{label}</Text>
        </View>
      )}

      {/* Native Select Container */}
      <View style={styles.selectContainer}>
        {Platform.OS === 'web' ? (
          // Web: Use native HTML select
          <select
            value={value !== null && value !== undefined ? String(value) : ''}
            onChange={handleChange}
            disabled={disabled}
            dir="rtl"
            style={{
              width: '100%',
              height: 44,
              padding: '10px 40px 10px 12px',
              border: error ? '1px solid #EF4444' : `1px solid ${themeColors.border}`,
              borderRadius: tokens.radii.absolute["radius-8"],
              backgroundColor: disabled ? themeColors.backgroundDisabled : themeColors.background,
              color: value ? themeColors.textPrimary : themeColors.textPlaceholder,
              fontSize: tokens.typography.paragraph["paragraph small font size"],
              fontFamily: FONTS.regular,
              textAlign: getTextAlign(isRTL),
              direction: 'rtl',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              outline: 'none',
            }}
            onFocus={(e) => {
              if (!error) {
                e.target.style.borderColor = COLORS.primary;
                e.target.style.borderWidth = '2px';
              }
            }}
            onBlur={(e) => {
              e.target.style.borderColor = error ? tokens.colors.raw.red["500"] : themeColors.border;
              e.target.style.borderWidth = '1px';
            }}
          >
            <option value="" disabled style={{ color: themeColors.textPlaceholder }}>
              {displayPlaceholder}
            </option>
            {options.map((option) => (
              <option
                key={String(option.value)}
                value={String(option.value)}
                style={{ color: themeColors.textPrimary }}
              >
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          // Native: Fallback text (use Picker from @react-native-picker/picker for native)
          <Text style={[styles.nativeFallback, { backgroundColor: themeColors.background, borderColor: themeColors.border, color: themeColors.textPrimary }]}>
            {options.find(opt => String(opt.value) === String(value))?.label || displayPlaceholder}
          </Text>
        )}

        {/* Arrow Icon handled by CSS background-image */}
      </View>

      {/* Error Message */}
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    width: '100%',
  },
  // Label
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  requiredStar: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.red["500"],
  },
  // Select Container
  selectContainer: {
    position: 'relative',
    width: '100%',
  },
  // Error
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },
  // Native Fallback
  nativeFallback: {
    height: 44,
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
});

export default SelectDropdown;

const styles = getStyles(true);
